/**
 * `projectDuplication.ts` — the server-side project clone, ported from
 * `Models/ProjectModel/ProjectDuplicationModel.cs`.
 *
 * Duplicating a project copies the project row, every scene / entity / asset
 * row beneath it and the S3 prefix. No client can do that in one atomic pass,
 * so Unity delegates to an admin lambda
 * (`anonOperation(operation:"projduplicate")`) and then WAITS for the clone to
 * appear. This module is that half of the flow — the mutation, the wait, and
 * the media backfill upstream had to add on top of it — kept out of
 * `HttpProjectListModel` because it is a long-running pipeline rather than a
 * catalog operation.
 *
 * Unity's OTHER duplication path (`DuplicateInApp`, a full client-side deep
 * copy via `ProjectDuplicator`) is deliberately NOT ported: it exists for SSO
 * accounts (`ICustomerModel.UseExternalIdentity`), and the web port does not
 * model external identity at all. If SSO accounts ever reach the web client
 * this is the gap to close — they would land on the server path, which is what
 * the desktop client uses for every non-SSO account.
 */
import type { HttpAdminStorageModel } from "@models/storage/HttpAdminStorageModel";
import type { IStorageModel } from "@models/storage/IStorageModel";
import type { ProjectDto } from "@models/data/projectDto";
import type { ProjectInfo } from "@models/data/projectInfo";
import type { SceneDto } from "@models/data/sceneDto";
import { randomUuid } from "@models/utils/uuid";
import {
  parseWorkflowDocumentText,
  serializeWorkflowDocument,
} from "@models/workflow/WorkflowYaml";

/** `ProjectDuplicationModel.k_maxDuplicateWaitSeconds` — 20 minutes. */
const K_MAX_DUPLICATE_WAIT_MS = 1_200_000;
/** `k_serverDuplicateSceneWaitSeconds`. */
const K_SCENE_WAIT_MS = 30_000;
/** Allow the cloned S3 tree to settle after the project row appears. */
const K_WORKFLOW_WAIT_MS = 30_000;
/** Unity's poll interval for both waits (`Task.Delay(2000)`). */
const K_POLL_INTERVAL_MS = 2_000;
/**
 * Unity's `await Task.Delay(1000)` between the clone appearing and reading it
 * back — "the project can sometimes be not ready for get"
 * (`ProjectDuplicationModel.cs:165-166`). Dynamo is read-after-write
 * consistent for a `GetItem`, but the lambda writes the row before it has
 * finished filling the rest of the project, so this is a settling window, not
 * a consistency workaround.
 */
const K_SETTLE_MS = 1_000;

export interface DuplicateOnServerArgs {
  readonly source: ProjectInfo;
  /** Minted by the CALLER — see `duplicateProjectAsync` for why. */
  readonly targetProjectId: string;
  readonly targetName: string;
  /** The signed-in account, which is where the clone lands. */
  readonly targetCustomerId: string;
  /** `UserInfo.FirstName` upstream; the web has one display name. */
  readonly author: string;
  readonly admin: HttpAdminStorageModel;
  readonly storage: IStorageModel;
}

/**
 * Kick off the clone and wait for it. Resolves with the new project row, or
 * throws when the lambda rejects the request or the clone never appears.
 *
 * Unity ignores the mutation's response and polls regardless; we treat a
 * rejection as fatal, because a request that never started would otherwise
 * hold the grid's "Duplicating" card for the full 20-minute timeout with
 * nothing to explain it.
 */
export async function duplicateProjectOnServerAsync(
  args: DuplicateOnServerArgs,
): Promise<ProjectDto> {
  const { succeeded, message } = await args.admin.duplicateProjectAsync({
    sourceCustomerId: args.source.customerId ?? args.targetCustomerId,
    projectId: args.source.id,
    newProjectId: args.targetProjectId,
    newName: args.targetName,
    author: args.author,
    sharedLinkId: args.source.sharedLinkId ?? null,
  });
  if (!succeeded) {
    throw new Error(
      `[projectDuplication] projduplicate refused the request${message ? `: ${message}` : ""}`,
    );
  }

  const { value: created } = await pollAsync(
    () => args.storage.getCustomerProject(args.targetCustomerId, args.targetProjectId),
    (row) => row !== null,
    K_MAX_DUPLICATE_WAIT_MS,
  );
  if (!created) {
    throw new Error(
      `[projectDuplication] duplicated project ${args.targetProjectId} did not appear within 20 minutes`,
    );
  }
  await delay(K_SETTLE_MS);
  return created;
}

/**
 * Gives a cloned `workflow.yaml` its own identity after the duplication lambda
 * copies the source project's S3 tree. Workflow summaries and learner state are
 * keyed by workflow uid rather than project id, so retaining the source uid
 * makes the two projects overwrite or reject each other's data.
 */
export async function rekeyDuplicatedWorkflowAsync(args: {
  readonly sourceProjectId: string;
  readonly sourceCustomerId: string;
  readonly targetProjectId: string;
  readonly targetProjectName: string;
  readonly targetCustomerId: string;
  readonly storage: IStorageModel;
}): Promise<void> {
  const sourceRawText = await args.storage.getWorkflowYaml(
    args.sourceCustomerId,
    args.sourceProjectId,
  );
  if (sourceRawText === null || sourceRawText.trim().length === 0) return;

  const { value: rawText } = await pollAsync(
    () => args.storage.getWorkflowYaml(args.targetCustomerId, args.targetProjectId),
    (text) => text !== null && text.trim().length > 0,
    K_WORKFLOW_WAIT_MS,
  );
  if (rawText === null || rawText.trim().length === 0) {
    throw new Error(
      "[projectDuplication] duplicated workflow did not appear within 30 seconds",
    );
  }

  const parsed = parseWorkflowDocumentText(rawText);
  if (parsed.document === null) {
    throw new Error(
      "[projectDuplication] cannot assign a new workflow uid because the duplicated workflow is unreadable",
    );
  }

  parsed.document.uid = randomUuid();
  parsed.document.projectId = args.targetProjectId;
  parsed.document.projectName = args.targetProjectName;
  await args.storage.putWorkflowYaml(
    args.targetCustomerId,
    args.targetProjectId,
    serializeWorkflowDocument(parsed.document),
  );
}

/**
 * Port of `BackfillMissingMediaDataForServerDuplicate` (`:272`) — the
 * workaround upstream had to add because `projduplicate` does not copy
 * `mediaasset` / `mediatile` rows. Without it a duplicated project loses every
 * text card, image and action button.
 *
 * Best-effort by design: the caller swallows failures (Unity logs a warning
 * and keeps the project), because a copy missing its media is still a copy.
 * Idempotent too — every leg skips ids the clone already has, so a re-run after
 * a partial failure cannot double-write.
 */
export async function backfillDuplicatedMediaAsync(args: {
  source: ProjectInfo;
  targetProjectId: string;
  targetCustomerId: string;
  storage: IStorageModel;
}): Promise<void> {
  const { storage, targetCustomerId, targetProjectId } = args;
  const sourceCustomerId = args.source.customerId ?? targetCustomerId;

  const [sourceMediaAssets, sourceScenes] = await Promise.all([
    storage.getMediaAssets(sourceCustomerId, args.source.id),
    storage.getScenes(sourceCustomerId, args.source.id),
  ]);
  if (sourceMediaAssets.length === 0 && sourceScenes.length === 0) return;

  const targetMediaAssets = await storage.getMediaAssets(targetCustomerId, targetProjectId);
  const seenAssetIds = new Set(targetMediaAssets.map((asset) => asset.ID).filter(Boolean));
  for (const asset of sourceMediaAssets) {
    if (!asset.ID?.trim() || seenAssetIds.has(asset.ID)) continue;
    seenAssetIds.add(asset.ID);
    await storage.createMediaAsset(targetCustomerId, targetProjectId, asset);
  }

  if (sourceScenes.length === 0) return;

  // The clone's scenes are written by the lambda, so they can lag the project
  // row that made us stop polling above. On timeout Unity backfills whatever
  // arrived rather than giving up on the media entirely.
  const { value: targetScenes, timedOut } = await pollAsync(
    () => storage.getScenes(targetCustomerId, targetProjectId),
    (scenes) => scenes.length >= sourceScenes.length,
    K_SCENE_WAIT_MS,
  );
  if (timedOut) {
    console.warn(
      `[projectDuplication] only found ${targetScenes.length} of ${sourceScenes.length} scene(s) in duplicated project ${targetProjectId} while backfilling media`,
    );
  }

  const sceneIdMap = mapSourceScenesToTarget(sourceScenes, targetScenes);

  for (const sourceScene of sourceScenes) {
    const targetSceneId = sceneIdMap.get(sourceScene.ID);
    if (!targetSceneId) {
      console.warn(
        `[projectDuplication] unable to map source scene ${sourceScene.ID} while backfilling media for ${targetProjectId}`,
      );
      continue;
    }

    const [sourceTiles, targetTiles] = await Promise.all([
      storage.getMediaTiles(sourceCustomerId, args.source.id, sourceScene.ID),
      storage.getMediaTiles(targetCustomerId, targetProjectId, targetSceneId),
    ]);
    const seenTileIds = new Set(targetTiles.map((tile) => tile.ID).filter(Boolean));
    for (const tile of sourceTiles) {
      if (!tile.ID?.trim() || seenTileIds.has(tile.ID)) continue;
      seenTileIds.add(tile.ID);
      await storage.createMediaTile(targetCustomerId, targetProjectId, targetSceneId, tile);
    }

    // The scene row's own `MediaAssetIDs` list is what the editor reads to know
    // which media to load, so copying the rows is not enough on its own. Unity
    // unions the source scene's list with the asset ids its TILES name, because
    // the two can disagree on a project written by an older client.
    const targetScene = targetScenes.find((scene) => scene.ID === targetSceneId);
    if (!targetScene) continue;
    const wanted = new Set<string>();
    for (const id of sourceScene.MediaAssetIDs ?? []) {
      if (id?.trim()) wanted.add(id);
    }
    for (const tile of sourceTiles) {
      if (tile.AssetID?.trim()) wanted.add(tile.AssetID);
    }
    if (wanted.size === 0) continue;

    const existing = targetScene.MediaAssetIDs ?? [];
    const merged = [...existing];
    for (const id of wanted) {
      if (!merged.includes(id)) merged.push(id);
    }
    if (merged.length === existing.length) continue;
    await storage.updateSceneMediaAssetIDs(
      targetCustomerId,
      targetProjectId,
      targetSceneId,
      merged,
    );
  }
}

/**
 * Port of `CreateSourceToTargetSceneMap` (`:433`) — three passes, each
 * claiming targets the earlier ones left: same id, then same index, then same
 * name. Three because the lambda's id policy is not part of its contract: it
 * may preserve scene ids or mint new ones, and matching on index or name is
 * how upstream copes either way.
 */
function mapSourceScenesToTarget(
  sourceScenes: ReadonlyArray<SceneDto>,
  targetScenes: ReadonlyArray<SceneDto>,
): Map<string, string> {
  const map = new Map<string, string>();
  const claimed = new Set<string>();
  const passes: ReadonlyArray<(a: SceneDto, b: SceneDto) => boolean> = [
    (a, b) => a.ID === b.ID,
    (a, b) => a.Index === b.Index,
    (a, b) => (a.Name ?? "") === (b.Name ?? ""),
  ];

  for (const isMatch of passes) {
    for (const source of sourceScenes) {
      if (!source.ID?.trim() || map.has(source.ID)) continue;
      const target = targetScenes.find(
        (scene) => scene.ID?.trim() && !claimed.has(scene.ID) && isMatch(source, scene),
      );
      if (!target) continue;
      map.set(source.ID, target.ID);
      claimed.add(target.ID);
    }
  }
  return map;
}

/**
 * Port of `ProjectsViewModel.GenerateNextCopyName` (`:1110`) — including its
 * regex, which reads oddly and is doing real work: an already-copied name
 * resumes numbering from its own suffix, so duplicating "Rig Copy 2" offers
 * "Rig Copy 3" rather than "Rig Copy 2 Copy".
 */
export function generateNextCopyName(
  sourceName: string,
  existingNames: Iterable<string>,
): string {
  const taken = new Set(existingNames);
  const match = /^(.*?)( Copy(?: (\d+))?)?$/.exec(sourceName);
  const rootName = match?.[1] ?? sourceName;
  let index = 0;
  if (match?.[2] !== undefined) index = match[3] !== undefined ? Number(match[3]) : 1;

  let candidate = "";
  do {
    candidate = index === 0 ? `${rootName} Copy` : `${rootName} Copy ${index}`;
    index += 1;
  } while (taken.has(candidate));
  return candidate;
}

/**
 * Unity's `do { … } while (secondsWaited < limit)` shape: the predicate is
 * checked BEFORE the first sleep, so a clone that is already there costs no
 * delay. The LAST read is returned either way — both callers have something
 * useful to do with a partial result — with `timedOut` saying which it is.
 */
async function pollAsync<T>(
  read: () => Promise<T>,
  isDone: (value: T) => boolean,
  timeoutMs: number,
): Promise<{ value: T; timedOut: boolean }> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const value = await read();
    if (isDone(value)) return { value, timedOut: false };
    if (Date.now() >= deadline) return { value, timedOut: true };
    await delay(K_POLL_INTERVAL_MS);
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
